import { useState, useEffect, type FormEvent } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { addCredits, type CreditAdjustmentType } from '@/lib/api'

interface AddCreditsModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  creditBalanceId: string
  organizationId: string
  serviceName: string
  accountName?: string | null
  currentBalance: number
  onSuccess: () => void
}

const ADJUSTMENT_TYPES: { value: CreditAdjustmentType; label: string; description: string }[] = [
  { value: 'bonus', label: 'Bonus', description: 'Free credits (promotion, goodwill)' },
  { value: 'correction', label: 'Correction', description: 'Fix balance error' },
  { value: 'refund', label: 'Refund', description: 'Refund for issue/complaint' },
]

export function AddCreditsModal({
  open,
  onOpenChange,
  creditBalanceId,
  organizationId,
  serviceName,
  accountName,
  currentBalance,
  onSuccess,
}: AddCreditsModalProps) {
  const queryClient = useQueryClient()

  // Form state
  const [amount, setAmount] = useState<string>('')
  const [type, setType] = useState<CreditAdjustmentType>('bonus')
  const [description, setDescription] = useState('')
  const [error, setError] = useState<string | null>(null)

  // Generate idempotency key when modal opens
  const [idempotencyKey, setIdempotencyKey] = useState<string>('')

  useEffect(() => {
    if (open) {
      // Reset form state and generate new idempotency key
      setAmount('')
      setType('bonus')
      setDescription('')
      setError(null)
      setIdempotencyKey(crypto.randomUUID())
    }
  }, [open])

  const mutation = useMutation({
    mutationFn: () =>
      addCredits(creditBalanceId, {
        amount: parseInt(amount, 10),
        type,
        description,
        idempotency_key: idempotencyKey,
      }),
    onSuccess: () => {
      // Invalidate organization query to refresh the data
      queryClient.invalidateQueries({ queryKey: ['organization', organizationId] })
      onSuccess()
      onOpenChange(false)
    },
    onError: (err: Error) => {
      setError(err.message)
    },
  })

  const parsedAmount = parseInt(amount, 10)
  const isValidAmount = !isNaN(parsedAmount) && parsedAmount !== 0
  const newBalance = isValidAmount ? currentBalance + parsedAmount : currentBalance
  const isValid = isValidAmount && description.trim().length > 0

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (!isValid) return
    setError(null)
    mutation.mutate()
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Adjust Credits</DialogTitle>
          <DialogDescription>
            Add or subtract credits for {serviceName}{accountName ? ` / ${accountName}` : ''}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Current Balance Display */}
          <div className="rounded-lg border bg-muted/50 p-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Current Balance</span>
              <span className="text-lg font-semibold">{currentBalance}</span>
            </div>
          </div>

          {/* Amount Input */}
          <div className="space-y-2">
            <Label htmlFor="amount">Amount</Label>
            <Input
              id="amount"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Enter amount (positive to add, negative to subtract)"
            />
            <p className="text-xs text-muted-foreground">
              Use positive numbers to add credits, negative to subtract
            </p>
          </div>

          {/* Type Select */}
          <div className="space-y-2">
            <Label htmlFor="type">Type</Label>
            <Select value={type} onValueChange={(value) => setType(value as CreditAdjustmentType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ADJUSTMENT_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    <div className="flex flex-col">
                      <span>{t.label}</span>
                      <span className="text-xs text-muted-foreground">{t.description}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description (required)</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Reason for this adjustment..."
              rows={3}
            />
          </div>

          {/* Preview */}
          {isValidAmount && (
            <div className="rounded-lg border p-4 space-y-2">
              <div className="flex justify-between items-center text-sm">
                <span className="text-muted-foreground">Current Balance</span>
                <span>{currentBalance}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-muted-foreground">Adjustment</span>
                <span className={parsedAmount > 0 ? 'text-green-600' : 'text-red-600'}>
                  {parsedAmount > 0 ? '+' : ''}{parsedAmount}
                </span>
              </div>
              <div className="border-t pt-2 flex justify-between items-center font-semibold">
                <span>New Balance</span>
                <span className={newBalance < 0 ? 'text-red-600' : ''}>{newBalance}</span>
              </div>
              {newBalance < 0 && (
                <p className="text-xs text-red-600">
                  Warning: This will result in a negative balance
                </p>
              )}
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!isValid || mutation.isPending}>
              {mutation.isPending ? 'Saving...' : 'Apply Adjustment'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
